import constants from "../../constants";
import lzstring from "../lz-string";
import storageUpgrades, { UpgraderFunc } from "./storage.upgrades";
import StorageBase, { _originalLocalStorage } from "./storage.base";
import { sendToErrorTracker } from "../error";
import { hashToArray } from "../vanilla";
import * as logger from "../logger";
import { DRIVE_STORAGE_PREFIX } from "../../store/storageConstants";

export type GetFunc = <T = string>(key: string, parseJson?: boolean, overrideUser?: string, forceKey?: boolean) => T | null;
export type SetFunc = (key: string, data: any, overrideUser?: string, forceKey?: boolean) => any;
export type RemoveFunc = (key: string, overrideUser?: string, forceKey?: boolean) => any;

export interface IStorage {
    /**
     * @deprecated Don't use this anymore. Use redux reducers instead of local storage
     * See MYG-77854
     */
    get: GetFunc;
    /**
     * @deprecated Don't use this anymore. Use redux reducers instead of local storage
     * See MYG-77854
     */
    set: SetFunc;
    /**
     * @deprecated Don't use this anymore. Use redux reducers instead of local storage
     * See MYG-77854
     */
    remove: RemoveFunc;
    /**
     * @deprecated Don't use this anymore. Use redux reducers instead of local storage
     * See MYG-77854
     */
    clear: (removePersisted?: boolean, overrideUser?: string, overridePrefix?: string) => IStorage;
    size: () => number;
    sizeInMB: () => number;
    getSizeDistribution: () => IIdDictionary<string>;
    removeAllKeysContainingStrings: (stringsToRemove: string[]) => void;
    getEntityCounts: () => IIdDictionary<number>;
}

export enum StorageKeys {
    ACTIVE_USERS_KEY = "@store_activeUsers",
    CACHE_KEY = "@store_cache",
    USER_LOGS_KEY = "@store_userHosLogs",
    SYSTEM_LOGS_KEY = "@store_systemHosLogs",
    USER_HOS_RULESETS_KEY = "@store_userHosRuleSets",
    DEVICE_LINKS_KEY = "@store_deviceLinks",
    DRIVE_API_CREDENTIALS_KEY = "@store_driveAPICredentials",
    DVIR_KEY = "@store_dvirLogs",
    USER_TRIPS_KEY = "@store_trips",
    TRIPANNOTATIONS_KEY = "@store_tripAnnotations",
    GROUPLINKEDDEVICES_KEY = "@store_groupLinkedDevices",
    FEEDVERSION_KEY = "@store_feedVersions",
    TIME_ZONE_INFO_KEY = "@store_timeZoneInfo",
    DAILY_ODOMETER_KEY = "@store_dailyOdometer",
    MESSAGES_KEY = "@store_messages",
    DRIVER_CHANGES_KEY = "@store_driverChanges",
    DEVICE_DEFECTLISTS_KEY = "@store_deviceDefectLists",
    TRAILER_DEFECTLISTS_KEY = "@store_trailerDefectLists",
    HOSANNOTATIONS_KEY = "@store_hosAnnotations",
    HISTORICAL_DAY_TRACKER_KEY = "@store_historicalDayTracker",
    ROUTES_KEY = "@store_routes",
    ERROR_KEY = "@store_error",
    TRAILER_ATTACHMENTS_KEY = "@store_trailerAttachments",
    SHIPMENTS_KEY = "@store_shipments",
    SUSPICIOUS_LOG_KEY = "@store_suspiciousLogsSent",
    OPENCAB_USERS_KEY = "@store_openCabUsers",
    OPENCAB_WORKFLOW_KEY = "@store_openCabWorkflow",
    OPENCAB_QUEUE_KEY = "@store_openCabQueue",
    IAM_CREDENTIALS_KEY = "@store_iamCredentials",
    SYSTEMSETTINGS_KEY = "@store_systemSettings",
    VEHICLE_HISTORY_KEY = "@store_vehicleHistory",
    TRANSLATIONS_KEY = "@store_translations",
    DRIVER_SEAT_USER = "@store_driverSeatUser",
    DATA_KEY = "@store_data",
    USERS_KEY = "@store_users"
}

const StorageKeysToCompress = {
    [`${ DRIVE_STORAGE_PREFIX }${ StorageKeys.USER_LOGS_KEY }`]: true,
    [`${ DRIVE_STORAGE_PREFIX }${ StorageKeys.SYSTEM_LOGS_KEY }`]: true
};

let walkmePrefixes = ["walkMe_", "wm-"];
let appVersionKey = "@appVersion";
let currentAppVersion: any;
let upgradeHasBeenDone = false;
let lzString = lzstring();
let storage: IStorage;
let textEncoder = new TextEncoder();

function getUsersObject() {
    return (StorageBase.getSystemUnsafe(StorageKeys.USERS_KEY, true) || {}) as IIdDictionary<IUser>;
}

function getActiveUserNames(): string[] {
    let activeUsersRaw = StorageBase.getSystemUnsafe<string>(StorageKeys.ACTIVE_USERS_KEY);
    let activeUsers: string[] = [];
    if (activeUsersRaw) {
        activeUsers = JSON.parse(activeUsersRaw);
    }
    return Array.isArray(activeUsers) ? activeUsers : [activeUsers];
}

function getUsers(activeOnly = false) {
    let users = getUsersObject();

    if (activeOnly) {
        let activeUserNames = getActiveUserNames();
        let activeUsersArray: IUser[] = [];

        activeUserNames.forEach(u => {
            if (users.hasOwnProperty(u)) {
                activeUsersArray.push(users[u]);
            }
        });

        return activeUsersArray;
    }

    return hashToArray(users);
}

export function getCurrentUser(): IUser | null {
    let users = getUsers(true);
    return users[0] || null;
}

export function handleParsingError(errorText: string, itemKey: string, currentUser?: IUser) {
    let user = itemKey !== StorageKeys.ACTIVE_USERS_KEY && itemKey !== StorageKeys.USERS_KEY && currentUser ;
    logger.error(logger.Source.STORAGE, `Storage: ${ errorText }`, user);
    // If we can't get current user then don't send report to error tracker, just log it (to prevent infinite loop)
    user && sendToErrorTracker(new Error(errorText), "javascript");
}

function handleParsingErrorAndRemove(errorText: string, itemKey: string, overrideUser?: string, forceKey?: boolean, currentUser?: IUser) {
    storage.remove(itemKey, overrideUser, forceKey);
    (itemKey === StorageKeys.USER_LOGS_KEY || itemKey === StorageKeys.SYSTEM_LOGS_KEY || itemKey === StorageKeys.DVIR_KEY) && storage.remove(StorageKeys.FEEDVERSION_KEY, overrideUser);
    handleParsingError(errorText, itemKey, currentUser);
}

export function handleLogCompression(item: string | null) {
    // If hos_logs was excessive, it would have been compressed and we need to decompress it, if it wasn't this will return null
    let decompressedLogs = (lzString.decompressFromUTF16(item) as string | null) || null;

    if (item && !decompressedLogs) {
        return null;
    }
    return  decompressedLogs;
}

let getPrefix = function (key: string, userId?: string) {
    if (key && key.replace(DRIVE_STORAGE_PREFIX, "").match(/^@/)) {
        // Don't use a user name prefix if it's a non-user value
        return DRIVE_STORAGE_PREFIX;
    }

    if (!userId) {
        // Get the current active user if the passed in user name is null or undefined
        let activeUsers = getActiveUserNames();
        let users = getUsersObject();
        let user;

        if (activeUsers && activeUsers[0]) {
            user = users[activeUsers[0]];
        }
        let activeUserId = user?.id;

        if (!activeUserId) {
            // If there is no active user, this probably creates a bad situation where we are setting user data to a table not prefixed by user
            // Should log this and check for occurrences on a regular basis
            logger.error(
                logger.Source.STORAGE,
                `Storage: Unable to implicitly get user prefix when setting/getting key "${ key }". ActiveUsers: ${ activeUsers.join(",") }. UsersKeys: ${ Object.keys(users).join(",") }`
            );
        }

        return `${ DRIVE_STORAGE_PREFIX }${ activeUserId ? `${ activeUserId }_` : "" }`;
    }

    // Use the passed in string
    return `${ DRIVE_STORAGE_PREFIX }${ userId }_`;
};

export let areLogsCompressed = (logs: string | null) => {
    if (!logs || logs === "{}" || logs.length === 0 || logs.indexOf("status") !== -1) {
      return false;
    }

    try {
		const parsedLogs = JSON.parse(logs);

        // If the JSON.parse succeeds and we have a set of empty dictionaries, for example `{id: {},  id2: {}, id3: {}}`
		if (Object.keys(parsedLogs).length) {
			return false;
		}

		return true;
	} catch (e) {
        // If we catch on the JSON.parse, then it's compressed
		return true;
	}
};

export function getFullKey (itemKey: string, overrideUser?: string, forceKey = false): string {
    let prefix = !forceKey ? getPrefix(itemKey, overrideUser) : "";
    return `${ prefix }${ itemKey }`;
}

let getAllKeys = () => Object.keys(_originalLocalStorage);

let getInternal = function <T = string>(itemKey: string, jsonParse = false, overrideUser?: string, forceKey = false) {
    let fullKey = getFullKey(itemKey, overrideUser, forceKey);
    let item = StorageBase.get(fullKey);
    let value: any = item;
    if (StorageKeysToCompress[fullKey]) {
        if (areLogsCompressed(item)) {
            value = handleLogCompression(item);
            if (!value) {
                 // we were unable to decompress encoded string, let's report it
                let currentUser = getCurrentUser();
                handleParsingErrorAndRemove(`Failed to decode string '${ itemKey }' from localStorage: ${ item }`, itemKey, overrideUser, forceKey, currentUser !== null ? currentUser : undefined);
                return null;
            }
        }
    }

    if (value && jsonParse) {
        try {
            return JSON.parse(value) as T;
        } catch (e) {
            let currentUser = getCurrentUser();
            handleParsingErrorAndRemove(`Unable to parse string '${ itemKey }' from localStorage: ${ value }`, itemKey, overrideUser, forceKey, currentUser !== null ? currentUser : undefined);
            return null;
        }
    } else {
        return value as T;
    }
};

let isCompressionSuccessful = (compressedLogs: string) => {
    let decompressedLogs = lzString.decompressFromUTF16(compressedLogs);
    return !!(decompressedLogs && decompressedLogs.indexOf("status") > 0);
};

let getCompressedHosLogs = (data: object) => lzString.compressToUTF16(JSON.stringify(data));

let countAllLogs = (data: object) => Object.keys(data).reduce((sum, curr) => sum + Object.keys(data[curr]).length, 0);

let compressHosLogs = (data: object) => {
    // Check if we need to compress logs
    if (countAllLogs(data) > constants.ALLOWED_UNCOMPRESSED_HOSLOGS_AMOUNT) {
        let compressedHosLogs = getCompressedHosLogs(data);
        if (isCompressionSuccessful(compressedHosLogs)) {
            return compressedHosLogs;
        }
    }

    return data;
};

let setInternal = (itemKey: string, data: string | number | object, overrideUser?: string, forceKey = false) => {
    let storageKeyToSet = getFullKey(itemKey, overrideUser, forceKey);
    // Excessive hos logs can cause local storage to exceed its quota
    // and this will continuously happen on an hos sync. We need to
    // compress the logs in this case.
    let thisData = StorageKeysToCompress[storageKeyToSet] && typeof data === "object"
        ? compressHosLogs(data)
        : data;

    StorageBase.set(storageKeyToSet, thisData);
};

// TODO: Disabled temporarily
// let getInteger = function(compressionLimitCustomCode: string): number {
//     let startIndex = compressionLimitCustomCode.indexOf("_") + 1;
//     let endIndex = startIndex;
//     while (endIndex < compressionLimitCustomCode.length && !isNaN(compressionLimitCustomCode[endIndex] as any)) {
//         endIndex++;
//     }
//     return parseInt(compressionLimitCustomCode.substring(startIndex, endIndex + 1));
// };

let removeInternal = function (key: string, overrideUser?: string, forceKey = false) {
    StorageBase.remove(getFullKey(key, overrideUser, forceKey));
    return this;
};

let removeAllKeysContainingStrings = function (stringsToRemove: string[]) {
    let keysInLocalStorage = getAllKeys();

    keysInLocalStorage.forEach((key) => {
        stringsToRemove.forEach(stringToRemove => {
            if (key.toLowerCase().indexOf(stringToRemove.toLowerCase()) !== -1) {
                localStorage.removeItem(key);
            }
        });
    });
};

// Any keys that begin with the prefixes returned will not be cleared by default.
export const getPersistentStoragePrefixes = () => [`${ DRIVE_STORAGE_PREFIX }@`, ...walkmePrefixes];

let clear = function (removePersisted = false, overrideUser?: string, overridePrefix?: string) {
    let ignoredPattern = new RegExp(`^(${ getPersistentStoragePrefixes().join("|") })`);
    for (let key in localStorage) {
        if (!localStorage.hasOwnProperty(key)) {
            continue;
        }

        if ((!removePersisted && key.match(ignoredPattern)) || key.indexOf("driveAPICredentials") !== -1) {
            continue;
        }

        let prefix = !overridePrefix ? getPrefix(key, overrideUser) : overridePrefix;
        if (key.indexOf(prefix) !== -1 || key.indexOf(DRIVE_STORAGE_PREFIX) === -1) {
            localStorage.removeItem(key);
        }
    }
    return this;
};

/**
 * Returns localStorage size in KB using accurate UTF-8 byte measurement.
 * This method uses TextEncoder for precise byte calculation, then converts to KB.
 *
 * @returns {number} Total localStorage size in kilobytes (precise)
 */
let size = function () {
    let totalBytes = 0;

    for (let i = 0; i < _originalLocalStorage.length; i++) {
        const key = _originalLocalStorage.key(i);
        if (key === null) {
            continue;
        }

        const value = _originalLocalStorage.getItem(key);
        if (value === null) {
            continue;
        }

        // TextEncoder is the best way to get accurate UTF-8 byte sizes
        totalBytes += textEncoder.encode(key).length;
        totalBytes += textEncoder.encode(value).length;
    }

    return totalBytes / 1024;
};

/**
 * Convenience method that returns localStorage size in megabytes
 *
 * @returns {number} Size in MB with 3 decimal places, trailing zeros removed
 */
let sizeInMB = function () {
    const kb = size();
    return parseFloat((kb / 1024).toFixed(3));
};
let getSizeFromLength = function (length: number) {
    let bits = length * 16;
    let value;
    let unit;

    if (bits >= 1025 * 512) {
        value = (bits / 8 / 1024 / 1024).toFixed(2);
        unit = "MB";
    } else if (bits >= 512) {
        value = (bits / 8 / 1024).toFixed(2);
        unit = "KB";
    } else {
        value = (bits / 8);
        unit = "B";
    }

    return `${ value }${ unit }`;
};
let getSizeDistribution = function () {
    return Object.keys(_originalLocalStorage)
        .map(key => {
            let value = _originalLocalStorage.getItem(key);

            return {
                key,
                size: value ? value.length : 0,
                value: value ? value : ""
            };
        })
        .sort((item1, item2) => item1.size > item2.size ? -1 : 1)
        // let's return sizes for 20 the most expensive keys
        .slice(0, 20)
        .reduce((result, item) => {
            result[item.key] = getSizeFromLength(item.size);
            if (item.key.indexOf("@store_data") !== -1 && item.value) {
                try {
                    let valueObj = JSON.parse(item.value);
                    for (let storageKey in valueObj) {
                        if (!valueObj.hasOwnProperty(storageKey)) {
                            continue;
                        }

                        let valObjectSize = valueObj ? JSON.stringify(valueObj[storageKey]).length : 0;
                        result[`${ item.key } > ${ storageKey }`] = getSizeFromLength(valObjectSize);
                    }
                } catch { }
            }
            return result;
        }, <IIdDictionary<string>>{});
};

let getEntityCounts = () => {
    let entityCounts = Object.keys(localStorage)
        .filter(itemKey => itemKey.indexOf(DRIVE_STORAGE_PREFIX) === 0 &&
            (itemKey.indexOf("_cache_") !== -1))
        .sort()
        .map(key => {
            let value = localStorage.getItem(key);
            let obj: {} | null = null;
            try {
                obj = value ? JSON.parse(value) : null;
            } catch { }
            return {
                key: key.replace(DRIVE_STORAGE_PREFIX, ""),
                count: obj ? Object.keys(obj).length : 0
            };
        })
        .reduce((result, item) => {
            result[item.key] = item.count;
            return result;
        }, <IIdDictionary<number>>{});

    let entitiesWithNestedDictionaries = [StorageKeys.HOSANNOTATIONS_KEY, StorageKeys.USER_LOGS_KEY, StorageKeys.SHIPMENTS_KEY, StorageKeys.SYSTEM_LOGS_KEY,
        StorageKeys.DVIR_KEY, StorageKeys.DRIVER_CHANGES_KEY, StorageKeys.TRAILER_ATTACHMENTS_KEY, StorageKeys.DEVICE_LINKS_KEY];
    entitiesWithNestedDictionaries.forEach(key => {
        let value = localStorage.getItem(DRIVE_STORAGE_PREFIX + key);
        let obj: {} | null = null;
        try {
            obj = value ? JSON.parse(value) : null;
        } catch { }
        let valueKeys = obj ? Object.keys(obj) : [];

        entityCounts[`${ key }_users`] = valueKeys.length;
        valueKeys.forEach(user => {
            entityCounts[`${ key }_${ user }`] = Object.keys((obj || {})[user] || {}).length;
        });
    });
    return entityCounts;
};

let storageUpgrader = function () {
    if (upgradeHasBeenDone) {
        return;
    }
    currentAppVersion = getInternal(appVersionKey, false);

    if (!currentAppVersion) {
        currentAppVersion = constants.APP_VERSION;
        setInternal(appVersionKey, currentAppVersion);
        return; // No upgrades needed
    }

    currentAppVersion = parseFloat(currentAppVersion);
    if (currentAppVersion === constants.APP_VERSION) {
        upgradeHasBeenDone = true;
        return; // Already on latest version
    }

    let [upgrades, downgrades, versionsToRunForEachUser] = storageUpgrades();
    let versions = Object.keys(upgrades);
    let storageRefresh = function (error?: Error) {
        for (let key in localStorage) {
            if (localStorage.hasOwnProperty(key) && key.indexOf(DRIVE_STORAGE_PREFIX) === 0) {
                localStorage.removeItem(key);
            }
        }

        let loggerMsg = `Reload APP_VERSION=${ constants.APP_VERSION } currentAppVersion=${ currentAppVersion }`;
        if (error) {
            loggerMsg += `${ error ? error.message : "" }`;
            logger.error(logger.Source.STORAGE, loggerMsg);
        } else {
            logger.log(logger.Source.STORAGE, loggerMsg);
        }

        window.location.reload();
    };
    let upgradeStorageToVersion = (method: UpgraderFunc, toVersion: string) => {
        try {
            //from version 0.28 onwards we're storing users in @store_users instead of @users
            let users = getInternal(currentAppVersion < 0.28 && StorageBase.keyExist("@users") ? "@users" : StorageKeys.USERS_KEY, true) || {};
            let shouldReloadPage = false;

            if (versionsToRunForEachUser.indexOf(parseFloat(toVersion)) !== -1) {
                for (let userkey in users) {
                    if (users.hasOwnProperty(userkey)) {
                        let u = users[userkey];
                        shouldReloadPage = shouldReloadPage || method(
                            (key, jsonParse, overrideUser?, forceKey?) => getInternal<any>(key, jsonParse, overrideUser || u.id, forceKey || false),
                            (key, data, overrideUser?, forceKey?) => setInternal(key, data, overrideUser || u.id, forceKey),
                            (key, overrideUser?, forceKey?) => removeInternal(key, overrideUser || u.id, forceKey || false),
                            (removePersisted) => clear(removePersisted, u.id)
                        );
                    }
                }
            } else {
                shouldReloadPage = shouldReloadPage || method(
                    (key, jsonParse, overrideUser?, forceKey?) => getInternal<any>(key, jsonParse, overrideUser || undefined, forceKey || false),
                    (key, data, overrideUser?, forceKey?) => setInternal(key, data, overrideUser || undefined, forceKey),
                    (key, overrideUser?, forceKey?) => removeInternal(key, overrideUser || undefined, forceKey || false),
                    (removePersisted) => clear(removePersisted, undefined)
                );
            }

            currentAppVersion = parseFloat(toVersion);
            setInternal(appVersionKey, currentAppVersion);

            if (shouldReloadPage) {
                logger.log(logger.Source.STORAGE, `Force reload app after storage upgrade toVersion=${ toVersion }`);
                window.location.hash = ""; // Go back to main screen TODO: Allow any page to be an entry point to the app later
                window.location.reload();
                return;
            }
        } catch (e) {
            // error has cyclic dependency from storage module
            sendToErrorTracker(e, "STORAGE");
            // TODO: Right now, just hard clear localStorage & reload the page if an error is encountered
            storageRefresh(e);
        }
    };
    let isUpgrade = currentAppVersion < constants.APP_VERSION;

    // If downgrading then start from the latest version
    versions.sort((a, b) => (parseFloat(a) - parseFloat(b)) * (isUpgrade ? 1 : -1));
    let upgraders = isUpgrade ? upgrades : downgrades;

    for (let i = 0; i < versions.length; i++) {
        let version = versions[i];
        let fVersion = parseFloat(version);
        let needUpgrade = isUpgrade ? fVersion <= constants.APP_VERSION && fVersion > currentAppVersion : fVersion > constants.APP_VERSION;
        if (upgraders.hasOwnProperty(version) && needUpgrade) {
            if (Math.floor(fVersion) !== Math.floor(currentAppVersion)) {
                storageRefresh();
                return;
            } else {
                let toVersion = isUpgrade ? version : (i < versions.length - 1 ? versions[i + 1] : "0");
                upgradeStorageToVersion(upgraders[version], toVersion);
            }
        }
    }

    // If we've iterated through the upgrades, and still are not on the latest version, update
    if (currentAppVersion < constants.APP_VERSION) {
        currentAppVersion = constants.APP_VERSION;
        setInternal(appVersionKey, currentAppVersion);
    }

    upgradeHasBeenDone = true;
};

storage = {
    get: function <T = string>(key: string, jsonParse?: boolean, overrideUser?: string, forceKey = false) {
        storageUpgrader();
        return getInternal<T>(key, jsonParse, overrideUser, forceKey);
    },
    set: function (itemKey: string, data: any, overrideUser?: string, forceKey = false) {
        storageUpgrader();
        setInternal(itemKey, data, overrideUser, forceKey);
    },
    remove: function (itemKey: string, overrideUser?: string, forceKey = false) {
        storageUpgrader();
        return removeInternal(itemKey, overrideUser, forceKey);
    },
    clear,
    size,
    sizeInMB,
    getSizeDistribution,
    removeAllKeysContainingStrings,
    getEntityCounts
};

export default storage;
